import { useState } from "react"

import {
  useCreateAiPolicy,
  useDeleteAiPolicy,
  useGetAiPolicies,
  useGetAiPolicyConditionOptions,
  useGetAiPolicyDecisions,
  useGetAiPolicyMcpServerScopeOptions,
  useGetAiPolicyScopeOptions,
  useListAiPolicies,
  useUpdateAiPolicy
} from "@/api/useAiPolicies"
import { isAiDiscoveryLive } from "@/domain/user"
import { useQueryString } from "@/hooks/useQueryString"
import { useFeatureFlags } from "@/hooks/useUser"
import {
  AI_POLICY_KINDS,
  getAiPolicies,
  getAiPolicyDecisionRows,
  getAiPolicyKind,
  getDecisionsPolicyId,
  getOptions,
  SCOPE_AGENT_OPTION,
  toAiPolicyInput
} from "./useLiveAiPolicies.selector"

import type { AiPolicy, AiPolicyInput } from "@/api/useAiPolicies.types"
import type { Permission } from "@/domain/user"
import type { AiPolicyDecisionFilters } from "./useLiveAiPolicies.types"

const DECISIONS_PAGE_SIZE = 50

// The largest page of decisions the API serves
export const MAX_DECISIONS_PAGE_SIZE = 500

// The API writes the policies with this permission
export const AI_POLICIES_WRITE_PERMISSION: Permission = {
  resource: "ai.policies",
  action: "write"
}

export const useLiveAiPolicies = () => {
  const { response, isLoading, isError, refetch } = useGetAiPolicies({
    select: getAiPolicies
  })
  const creator = useCreateAiPolicy()
  const updater = useUpdateAiPolicy()
  const remover = useDeleteAiPolicy()
  const update = (id: string, policy: AiPolicyInput) =>
    updater.mutateAsync({ needle: `/${id}`, data: { policy } })

  return {
    policies: response ?? [],
    isLoading,
    isError,
    refetch,
    create: (policy: AiPolicyInput) => creator.mutateAsync({ policy }),
    update,
    remove: (id: string) => remover.mutateAsync(id),
    setEnabled: (policy: AiPolicy, enabled: boolean) =>
      update(policy.id, { ...toAiPolicyInput(policy), enabled })
  }
}

// The list's filters, by the query string keys the filter bar sets, which are
// the ones the API takes
export const AI_POLICIES_FILTER_KEYS = [
  "name",
  "kind",
  "enforcement",
  "status",
  "platform",
  "agentId",
  "toolName",
  "toolCategory",
  "connector",
  "knowledgeSource",
  "violation"
] as const

// The days back the list counts a policy's violations over, the API's default
export const AI_POLICIES_VIOLATIONS_WINDOW_DAYS = 30

// The list's filters and order, from the query string; none when it asks for
// neither, and the list is every policy in priority order
export const useAiPoliciesListParams = () => {
  const {
    pageNumber: _pageNumber,
    query: _query,
    ...params
  } = useQueryString([...AI_POLICIES_FILTER_KEYS, "orderBy", "ascending"])
  const entries = Object.entries(params).filter(
    (entry): entry is [string, string] => !!entry[1]
  )

  return entries.length ? Object.fromEntries(entries) : undefined
}

// The policies the API filtered and ordered as the list asks
export const useListedAiPolicies = (
  params: Record<string, string> | undefined
) => {
  const { response, isLoading } = useListAiPolicies(params, {
    select: getAiPolicies
  })

  return {
    policies: params ? response : undefined,
    isLoading: !!params && isLoading
  }
}

// The root agents a policy can name, by their api-agents id
export const useAiPolicyAgentOptions = () => {
  const { response, isLoading } = useGetAiPolicyScopeOptions()

  return { isLoading, options: getOptions(response, SCOPE_AGENT_OPTION) }
}

// The MCP servers an MCP server policy can name, with where each runs
export const useAiPolicyMcpServers = (enabled = true) => {
  const { response, isLoading } = useGetAiPolicyMcpServerScopeOptions({
    enabled
  })

  return { isLoading, servers: response?.data ?? [] }
}

export const useAiPolicyConditionOptions = () => {
  const { response } = useGetAiPolicyConditionOptions()

  return {
    toolCategories: getOptions(response, "toolCategories"),
    toolSources: getOptions(response, "toolSources"),
    connectors: getOptions(response, "connectors")
  }
}

// The violations page by page: the decisions are violations only. The API
// pages with tokens, so the tokens of the
// pages before are kept to go back, and new filters start from the first page.
// The API serves decisions only where AI Discovery is live
export const useAiPolicyDecisions = (
  filters: AiPolicyDecisionFilters,
  { pageSize = DECISIONS_PAGE_SIZE }: { pageSize?: number } = {}
) => {
  const isServed = isAiDiscoveryLive(useFeatureFlags("aiDiscovery"))
  const { policyId, policyKind, ...narrowedBy } = filters
  const policies = useGetAiPolicies({
    select: getAiPolicies,
    enabled: isServed
  })
  // Policy kinds, comma-separated, ask the decisions of their policies alone:
  // the API takes policies, not kinds
  const kinds = AI_POLICY_KINDS.filter((kind) =>
    (policyKind ?? "").split(",").includes(kind)
  )
  const kindPolicies = (policies.response ?? []).filter(
    (policy) => !kinds.length || kinds.includes(getAiPolicyKind(policy))
  )
  const decisionsPolicyId = getDecisionsPolicyId(
    policyId,
    policies.response ?? [],
    kinds
  )
  // The page tokens hold for the policies, filters and order they were asked
  // by: a policy added or removed starts from the first page too
  const filtersKey = JSON.stringify([decisionsPolicyId, narrowedBy])
  const [paging, setPaging] = useState<{
    filtersKey: string
    tokens: string[]
  }>({ filtersKey, tokens: [] })

  // New filters drop the pages of the filters before, so going back to those
  // filters starts from their first page too
  if (paging.filtersKey !== filtersKey) setPaging({ filtersKey, tokens: [] })

  const tokens = paging.filtersKey === filtersKey ? paging.tokens : []
  const setTokens = (next: string[]) => setPaging({ filtersKey, tokens: next })
  const pageToken = tokens[tokens.length - 1]
  const decisions = useGetAiPolicyDecisions(
    {
      pageSize,
      violationsOnly: true,
      policyId: decisionsPolicyId,
      ...narrowedBy,
      pageToken
    },
    { enabled: isServed && !!decisionsPolicyId }
  )
  const { response, isFetching } = decisions
  const isLoading = policies.isLoading || decisions.isLoading
  // A failed refetch of policies already loaded keeps the decisions shown
  const isError = (policies.isError && !policies.response) || decisions.isError
  // A failed page shows no rows, not the page before it; with no policy there
  // are no decisions to show
  const page = isError || !decisionsPolicyId ? undefined : response
  const nextPageToken = page?.nextPageToken
  const rows = getAiPolicyDecisionRows(
    page?.decisions ?? [],
    policies.response ?? []
  )
  const first = tokens.length * pageSize + 1

  return {
    isServed,
    rows,
    // The policies the decisions can be narrowed to: those of the kinds asked
    policies: kindPolicies,
    arePoliciesLoading: isServed && policies.isLoading,
    isLoading: isServed && (isLoading || isFetching),
    isError,
    // Retries what failed: the policies the decisions are asked by, or the page
    refetch: decisions.isError ? decisions.refetch : policies.refetch,
    page: tokens.length + 1,
    // The API counts no total: the range is the shown page's
    range: rows.length
      ? { from: first, to: first + rows.length - 1 }
      : undefined,
    // The shown page is the one before while the next loads: no second click
    // pages from it again
    hasPrevious: !isFetching && tokens.length > 0,
    hasNext: !isFetching && !!nextPageToken,
    previous: () => setTokens(tokens.slice(0, -1)),
    next: () => nextPageToken && setTokens([...tokens, nextPageToken])
  }
}
